import { useEffect, useState } from 'react'
import { useHash, parseRoute, navigate } from './router'
import { api } from './api'
import { setAppTimezone } from './datetime'
import { Toaster } from './toast'
import Sidebar from './components/Sidebar'
import ClientList from './components/ClientList'
import ClientSettings from './components/ClientSettings'
import Tracker from './components/Tracker'
import InvoiceList from './components/InvoiceList'
import InvoiceView from './components/InvoiceView'
import GlobalSettings from './components/GlobalSettings'
import Dashboard from './components/Dashboard'
import Today from './components/Today'
import Todos from './components/Todos'
import Notes from './components/Notes'
import ChatScreen, { ChatLauncher } from './components/Chat'
import { setRouteContextProvider } from './chat'
import { installShortcuts } from './shortcuts'
import ShortcutSettings from './components/ShortcutSettings'

export default function App() {
  const hash = useHash()
  const route = parseRoute(hash)

  const [navOpen, setNavOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  useEffect(() => setNavOpen(false), [hash]) // close the mobile drawer on navigation

  useEffect(() => installShortcuts(() => setHelpOpen((v) => !v)), [])
  useEffect(() => setHelpOpen(false), [hash])

  // Apply the saved timezone app-wide (defaults to the browser zone until loaded).
  useEffect(() => {
    api.getSettings().then((s) => setAppTimezone(s.timezone)).catch(() => {})
  }, [])

  // Tell the assistant which screen the user is on, so "log two hours on this"
  // resolves without naming the client. Delivered server-side as a
  // mid-conversation system message, which leaves the cached prefix intact.
  useEffect(() => {
    setRouteContextProvider(() => {
      const r = parseRoute(window.location.hash || '#/')
      switch (r.name) {
        case 'tracker':
          return `The user is viewing the time grid for client id ${r.id}.`
        case 'client-settings':
          return `The user is viewing settings for client id ${r.id}.`
        case 'client-invoices':
          return `The user is viewing invoices for client id ${r.id}.`
        case 'today':
          return 'The user is viewing the Today calendar.'
        case 'todos':
          return r.id != null ? `The user is viewing to-dos for client id ${r.id}.` : 'The user is viewing all to-dos.'
        case 'notes':
          return r.id != null ? `The user is viewing note page id ${r.id}.` : 'The user is viewing Notes.'
        default:
          return undefined
      }
    })
  }, [])

  // The invoice view is a standalone, print-focused screen — no app chrome.
  if (route.name === 'invoice') {
    return (
      <>
        <InvoiceView invoiceId={route.id!} />
        <Toaster />
      </>
    )
  }

  let screen: React.ReactNode
  switch (route.name) {
    case 'dashboard':
      screen = <Dashboard />
      break
    case 'today':
      screen = <Today />
      break
    case 'todos':
      screen = <Todos initialClientId={route.id} />
      break
    case 'notes':
      screen = <Notes noteId={route.id} />
      break
    case 'client-notes':
      screen = <Notes clientScope={route.id} />
      break
    case 'chat':
      screen = <ChatScreen conversationId={route.id} />
      break
    case 'clients':
      screen = <ClientList />
      break
    case 'client-new':
      screen = <ClientSettings mode="create" />
      break
    case 'client-settings':
      screen = <ClientSettings mode="edit" clientId={route.id!} />
      break
    case 'tracker':
      screen = <Tracker clientId={route.id!} />
      break
    case 'client-invoices':
      screen = <InvoiceList clientId={route.id!} />
      break
    case 'settings':
      screen = <GlobalSettings />
      break
    default:
      screen = (
        <div className="mx-auto max-w-md p-12 text-center text-slate-500">
          <p>Nothing here.</p>
          <button className="mt-4 text-[var(--accent)] underline" onClick={() => navigate('#/')}>
            Back to dashboard
          </button>
        </div>
      )
  }

  return (
    <div className="min-h-screen lg:flex">
      <Sidebar active={route.name} open={navOpen} onClose={() => setNavOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <div className="no-print flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-2 lg:hidden">
          <button className="rounded p-1.5 text-slate-600 hover:bg-slate-100" onClick={() => setNavOpen(true)} aria-label="Open menu">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
              <line x1="3" y1="5.5" x2="17" y2="5.5" />
              <line x1="3" y1="10" x2="17" y2="10" />
              <line x1="3" y1="14.5" x2="17" y2="14.5" />
            </svg>
          </button>
          <span className="flex items-center gap-2 font-semibold text-slate-900">
            <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-900 text-xs font-bold text-white">⏱</span>
            Time Tracker
          </span>
        </div>
        <main className="min-w-0 flex-1">{screen}</main>
      </div>
      {/* Deliberately outside the invoice route above — that screen prints. */}
      <ChatLauncher />
      {helpOpen && <ShortcutSettings onClose={() => setHelpOpen(false)} />}
      <Toaster />
    </div>
  )
}
